import React, { useState, useContext } from 'react';
import { AppContext } from '../../context/AppContext';
import { 
  GitBranch, 
  Building2, 
  UserCheck, 
  ShieldCheck, 
  ChevronRight, 
  Settings, 
  Plus, 
  Trash2, 
  CheckCircle2, 
  ArrowRight, 
  UserPlus, 
  Layers, 
  Move, 
  Edit3, 
  Check, 
  Sparkles,
  GripVertical,
  Users,
  ArrowUp,
  ArrowDown,
  RotateCcw,
  Save,
  X,
  AlertCircle
} from 'lucide-react';

const OrganizationManagement = () => {
  const { 
    users, 
    updateEmployeeInfo,
    deptLeads, 
    updateDeptLead, 
    approvalPresets, 
    setApprovalPresets,
    secondaryApproverIds,
    addSecondaryApprover,
    removeSecondaryApprover,
    deptList,
    setDeptList,
    rankApprovalRules,
    updateRankApprovalRules,
    resetRankApprovalRules,
    DEFAULT_RANK_APPROVAL_RULES
  } = useContext(AppContext);

  const [activeTab, setActiveTab] = useState('tree'); // 'tree' | 'rules' | 'presets'
  const [newPresetName, setNewPresetName] = useState('');
  const [newPresetDesc, setNewPresetDesc] = useState('');

  // Rank Approval Rules Editor State
  const [editingRule, setEditingRule] = useState(null);
  const [isEditRuleModalOpen, setIsEditRuleModalOpen] = useState(false);
  const [isAddRuleModalOpen, setIsAddRuleModalOpen] = useState(false);
  const [newRuleForm, setNewRuleForm] = useState({
    name: '',
    description: '',
    targetRanksText: '',
    stages: [
      { id: 'stg-1', step: 1, roleName: '1차 결재자 (소속팀장)', approverType: 'DEPT_LEAD', specificUserId: null },
      { id: 'stg-2', step: 2, roleName: '최종 결재자 (대표이사)', approverType: 'CEO', specificUserId: null }
    ]
  });

  // Manageable department list (moved to AppContext)
  const [isAddDeptModalOpen, setIsAddDeptModalOpen] = useState(false);
  const [newDeptNameInput, setNewDeptNameInput] = useState('');
  const [editingDeptName, setEditingDeptName] = useState(null);
  const [renamedDeptInput, setRenamedDeptInput] = useState('');

  // Multiple 2nd-stage Approvers Modal State (Requirement 4)
  const [isAddSecondaryModalOpen, setIsAddSecondaryModalOpen] = useState(false);
  const [selectedSecondaryCandidate, setSelectedSecondaryCandidate] = useState('');

  // Drag and drop state
  const [draggedUserId, setDraggedUserId] = useState(null);
  const [dragOverDept, setDragOverDept] = useState(null);

  // CEO and Directors
  const ceo = users.find(u => u.Account === 'chris' || u.JobTitle === '대표이사' || u.UserID === 'emp-10') || { Name: '이용관', JobTitle: '대표이사', Email: 'chris@clouflake.com' };

  // Add new department
  const handleAddDepartment = (e) => {
    e.preventDefault();
    const name = newDeptNameInput.trim();
    if (!name) return;
    if (deptList.includes(name)) {
      alert('이미 존재하는 부서명입니다.');
      return;
    }
    setDeptList(prev => [...prev, name]);
    setNewDeptNameInput('');
    setIsAddDeptModalOpen(false);
  };

  // Rename department
  const handleRenameDepartment = (oldName) => {
    const newName = renamedDeptInput.trim();
    if (!newName || newName === oldName) {
      setEditingDeptName(null);
      return;
    }
    setDeptList(prev => prev.map(d => d === oldName ? newName : d));
    // Update all users in this department
    users.filter(u => u.Department === oldName).forEach(u => {
      updateEmployeeInfo(u.UserID, { Department: newName });
    });
    setEditingDeptName(null);
  };

  // Delete department
  const handleDeleteDepartment = (deptToDelete) => {
    if (deptList.length <= 1) {
      alert('최소 1개 이상의 부서가 존재해야 합니다.');
      return;
    }
    if (confirm(`'${deptToDelete}' 부서를 삭제하시겠습니까? 소속 임직원은 '클라우드네이티브' 부서로 자동 이전됩니다.`)) {
      const fallbackDept = deptList.find(d => d !== deptToDelete) || '클라우드네이티브';
      users.filter(u => u.Department === deptToDelete).forEach(u => {
        updateEmployeeInfo(u.UserID, { Department: fallbackDept });
      });
      setDeptList(prev => prev.filter(d => d !== deptToDelete));
    }
  };

  // Drag and Drop Handlers
  const handleDragStart = (e, userId) => {
    e.dataTransfer.setData('text/plain', userId);
    setDraggedUserId(userId);
  };

  const handleDragOver = (e, dept) => {
    e.preventDefault();
    if (dragOverDept !== dept) {
      setDragOverDept(dept);
    }
  };

  const handleDragLeave = (dept) => {
    if (dragOverDept === dept) {
      setDragOverDept(null);
    }
  };

  const handleDrop = (e, targetDept) => {
    e.preventDefault();
    const userId = e.dataTransfer.getData('text/plain') || draggedUserId;
    if (userId && targetDept) {
      updateEmployeeInfo(userId, { Department: targetDept });
    }
    setDragOverDept(null);
    setDraggedUserId(null);
  };

  // Add custom preset
  const handleAddPreset = (e) => {
    e.preventDefault();
    if (!newPresetName) return;
    const newPreset = {
      id: `preset-${Date.now()}`,
      name: newPresetName,
      description: newPresetDesc || '관리자 수동 등록 결재선 템플릿',
      isDefault: false
    };
    setApprovalPresets(prev => [...prev, newPreset]);
    setNewPresetName('');
    setNewPresetDesc('');
    alert(`새 결재선 템플릿 '${newPresetName}'이 등록되었습니다.`);
  };

  const handleDeletePreset = (id) => {
    if (confirm('이 결재선 템플릿을 삭제하시겠습니까?')) {
      setApprovalPresets(prev => prev.filter(p => p.id !== id));
    }
  };

  return (
    <div className="space-y-6 text-left">
      {/* Top Title */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 pb-2 border-b border-slate-100">
        <div>
          <h2 className="text-xl font-bold text-slate-900 flex items-center gap-2">
            <GitBranch className="w-5 h-5 text-indigo-600" />
            <span>조직도 및 다단계 결재선(Approval Line) 관리</span>
          </h2>
          <p className="text-xs md:text-sm text-slate-500 mt-1">
            사내 직급/부서별 자동 결재선(팀장 ➔ 이사 ➔ 대표이사)을 설정하고 부서를 드래그앤드롭으로 유연하게 편성합니다.
          </p>
        </div>

        {/* Tab Buttons */}
        <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200 text-xs font-bold">
          <button
            onClick={() => setActiveTab('tree')}
            className={`px-3.5 py-1.5 rounded-lg transition-all cursor-pointer ${
              activeTab === 'tree' ? 'bg-white text-indigo-600 shadow-2xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            드래그앤드롭 조직도
          </button>
          <button
            onClick={() => setActiveTab('rules')}
            className={`px-3.5 py-1.5 rounded-lg transition-all cursor-pointer ${
              activeTab === 'rules' ? 'bg-white text-indigo-600 shadow-2xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            부서별 결재선 규칙
          </button>
          <button
            onClick={() => setActiveTab('presets')}
            className={`px-3.5 py-1.5 rounded-lg transition-all cursor-pointer ${
              activeTab === 'presets' ? 'bg-white text-indigo-600 shadow-2xs' : 'text-slate-600 hover:text-slate-900'
            }`}
          >
            수동 결재선 템플릿
          </button>
        </div>
      </div>

      {/* Tab 1: Organization Hierarchy Tree with Drag & Drop */}
      {activeTab === 'tree' && (
        <div className="space-y-6">
          {/* Drag & Drop Guidance Banner */}
          <div className="bg-indigo-50/70 border border-indigo-200 p-3.5 rounded-2xl flex items-center justify-between text-xs text-indigo-900">
            <div className="flex items-center gap-2 font-medium">
              <Move className="w-4 h-4 text-indigo-600 flex-shrink-0 animate-bounce" />
              <span>
                <strong>드래그 & 드롭 조직 개편</strong>: 사원 뱃지를 마우스로 끌어서 원하는 부서 박스에 떨어뜨리면 즉시 소속 부서가 변경됩니다.
              </span>
            </div>
            <button
              onClick={() => setIsAddDeptModalOpen(true)}
              className="px-3 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl font-bold cursor-pointer transition-all flex items-center gap-1 shadow-xs ml-2 flex-shrink-0"
            >
              <Plus className="w-3.5 h-3.5" />
              <span>새 부서 추가</span>
            </button>
          </div>

          {/* Level 1: CEO & Multiple 2nd-Stage Approvers (Directors) - Requirement 4 */}
          <div className="bg-gradient-to-br from-indigo-50/50 to-slate-50 border border-indigo-100 p-6 rounded-3xl space-y-4">
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-2">
              <span className="text-xs font-bold text-indigo-700 uppercase tracking-wider block">
                🏢 최고 의사결정권자 및 2차 결재권자 (Executive & Approver Line)
              </span>
              <span className="text-xs text-slate-500 font-medium">
                2차 승인자는 복수 지정 가능하며, 기안 시 순차 또는 전결 검토를 진행합니다.
              </span>
            </div>

            {/* CEO Card */}
            <div className="bg-white border-2 border-indigo-200 p-4 rounded-2xl shadow-xs flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-indigo-600 text-white font-black flex items-center justify-center text-sm shadow-sm">
                  {ceo.Name.charAt(0)}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <strong className="text-base text-slate-900">{ceo.Name}</strong>
                    <span className="px-2 py-0.5 rounded-md bg-indigo-100 text-indigo-800 font-bold text-xs">대표이사</span>
                  </div>
                  <span className="text-xs text-slate-500 font-mono">{ceo.Email} (최종 결재권자)</span>
                </div>
              </div>
              <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                최종 승인 (3차)
              </span>
            </div>

            {/* 2nd-Stage Approvers Section (Requirement 4) */}
            <div className="space-y-3 pt-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4 text-slate-700" />
                  <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                    2차 결재권자 목록 ({secondaryApproverIds.length}명)
                  </span>
                </div>
                <button
                  type="button"
                  onClick={() => setIsAddSecondaryModalOpen(true)}
                  className="px-3 py-1.5 bg-white border border-indigo-200 hover:border-indigo-400 text-indigo-600 hover:bg-indigo-50 rounded-xl text-xs font-bold transition-all cursor-pointer flex items-center gap-1 shadow-2xs"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>2차 승인자 추가</span>
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {secondaryApproverIds.map(secId => {
                  const secUser = users.find(u => u.UserID === secId) || { UserID: secId, Name: '이사', JobTitle: '이사', Email: 'director@clouflake.com', Department: 'AI서비스개발' };
                  return (
                    <div key={secId} className="bg-white border border-slate-200 p-4 rounded-2xl shadow-xs flex items-center justify-between hover:border-slate-300 transition-all">
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-2xl bg-slate-800 text-white font-black flex items-center justify-center text-sm shadow-xs">
                          {secUser.Name.charAt(0)}
                        </div>
                        <div>
                          <div className="flex items-center gap-2">
                            <strong className="text-sm md:text-base text-slate-900">{secUser.Name}</strong>
                            <span className="px-2 py-0.5 rounded-md bg-slate-100 text-slate-700 font-bold text-xs">
                              {secUser.JobTitle || secUser.Position || '이사'}
                            </span>
                            <span className="text-xs text-slate-400">({secUser.Department})</span>
                          </div>
                          <span className="text-xs text-slate-500 font-mono">{secUser.Email}</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-slate-100 text-slate-700">
                          2차 승인
                        </span>
                        {secondaryApproverIds.length > 1 && (
                          <button
                            type="button"
                            onClick={() => {
                              if (confirm(`'${secUser.Name}' 님을 2차 승인자에서 제외하시겠습니까?`)) {
                                removeSecondaryApprover(secId);
                              }
                            }}
                            className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded-lg transition-colors cursor-pointer"
                            title="2차 승인자 삭제"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Level 2: Departments & Team Leaders Grid (Drop Targets) */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-bold text-slate-700 uppercase tracking-wider">
                부서별 조직 구성 및 소속 임직원 배치 현황
              </h3>
              <span className="text-xs text-slate-400">총 {deptList.length}개 부서</span>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {deptList.map(dept => {
                const deptMembers = users.filter(u => u.Department === dept && u.EmploymentStatus !== '퇴사' && u.Status !== 'Retired');
                const activeLead = deptMembers.find(u => u.UserID === deptLeads[dept]) || deptMembers.find(u => (u.JobTitle || '').includes('팀장') || (u.Position || '').includes('이사') || (u.Position || '').includes('팀장')) || deptMembers[0];
                const leadUserId = activeLead?.UserID || 'emp-01';
                const leadUser = users.find(u => u.UserID === leadUserId && u.EmploymentStatus !== '퇴사') || deptMembers[0];
                const isDropTarget = dragOverDept === dept;

                return (
                  <div 
                    key={dept} 
                    onDragOver={(e) => handleDragOver(e, dept)}
                    onDragLeave={() => handleDragLeave(dept)}
                    onDrop={(e) => handleDrop(e, dept)}
                    className={`bg-white rounded-2xl p-5 shadow-xs space-y-3 transition-all border-2 ${
                      isDropTarget 
                        ? 'border-indigo-500 bg-indigo-50/40 ring-4 ring-indigo-100 scale-[1.01]' 
                        : 'border-slate-200 hover:border-slate-300'
                    }`}
                  >
                    {/* Department Card Header with Edit/Delete */}
                    <div className="flex justify-between items-center pb-2 border-b border-slate-100">
                      <div className="flex items-center gap-2">
                        <Building2 className="w-4 h-4 text-indigo-600" />
                        {editingDeptName === dept ? (
                          <div className="flex items-center gap-1">
                            <input
                              type="text"
                              value={renamedDeptInput}
                              onChange={e => setRenamedDeptInput(e.target.value)}
                              className="px-2 py-0.5 border border-indigo-400 rounded text-sm font-bold"
                              autoFocus
                            />
                            <button
                              onClick={() => handleRenameDepartment(dept)}
                              className="p-1 text-emerald-600 hover:text-emerald-700 cursor-pointer"
                            >
                              <Check className="w-4 h-4" />
                            </button>
                          </div>
                        ) : (
                          <span className="font-bold text-slate-900 text-base">
                            {dept}
                          </span>
                        )}
                      </div>

                      <div className="flex items-center gap-1.5">
                        <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-slate-100 text-slate-600">
                          {deptMembers.length}명
                        </span>
                        <button
                          onClick={() => {
                            setEditingDeptName(dept);
                            setRenamedDeptInput(dept);
                          }}
                          title="부서명 수정"
                          className="p-1 text-slate-400 hover:text-slate-700 cursor-pointer"
                        >
                          <Edit3 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDeleteDepartment(dept)}
                          title="부서 삭제"
                          className="p-1 text-slate-400 hover:text-rose-600 cursor-pointer"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Department Head Selector (1st Approver) */}
                    <div className="bg-indigo-50/60 p-3 rounded-xl border border-indigo-100 space-y-1.5">
                      <div className="flex items-center justify-between">
                        <span className="text-[10px] font-bold text-indigo-700 uppercase tracking-wider block">
                          👑 1차 결재권자 (부서장 / 팀장):
                        </span>
                        <span className="text-[10px] text-indigo-600 font-medium">1단계 전담 승인</span>
                      </div>
                      <select
                        value={leadUserId}
                        onChange={(e) => {
                          updateDeptLead(dept, e.target.value);
                          alert(`[${dept}] 부서의 1차 결재자가 변경되었습니다.`);
                        }}
                        className="w-full px-2.5 py-1.5 bg-white border border-indigo-200 rounded-lg text-xs font-bold text-slate-800 cursor-pointer focus:outline-none focus:border-indigo-500"
                      >
                        {deptMembers.map(m => (
                          <option key={m.UserID} value={m.UserID}>
                            {m.Name} ({m.Position || m.JobTitle}) - {m.Account}
                          </option>
                        ))}
                        {deptMembers.length === 0 && (
                          <option value="">소속 직원이 없습니다</option>
                        )}
                      </select>
                    </div>

                    {/* Team Members List (Draggable Cards) */}
                    <div className="space-y-1.5 pt-1">
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] font-bold text-slate-400">
                          소속 팀원 ({deptMembers.length}명) - 드래그하여 이동:
                        </span>
                        {isDropTarget && (
                          <span className="text-[10px] font-bold text-indigo-600 animate-pulse">
                            여기에 놓으면 부서 이동 완료 📥
                          </span>
                        )}
                      </div>

                      <div className="flex flex-wrap gap-2 min-h-[50px] p-2 bg-slate-50/60 border border-dashed border-slate-200 rounded-xl">
                        {deptMembers.map(m => {
                          const isBeingDragged = draggedUserId === m.UserID;
                          return (
                            <div
                              key={m.UserID}
                              draggable={true}
                              onDragStart={(e) => handleDragStart(e, m.UserID)}
                              onDragEnd={() => {
                                setDraggedUserId(null);
                                setDragOverDept(null);
                              }}
                              className={`text-xs px-3 py-1.5 rounded-xl border font-medium flex items-center gap-1.5 cursor-grab active:cursor-grabbing select-none transition-all shadow-2xs ${
                                isBeingDragged
                                  ? 'opacity-40 scale-95 bg-indigo-100 border-indigo-400'
                                  : m.UserID === leadUserId
                                  ? 'bg-indigo-50 border-indigo-200 text-indigo-800 font-bold hover:border-indigo-400'
                                  : 'bg-white border-slate-200 text-slate-800 hover:border-indigo-300 hover:bg-slate-50'
                              }`}
                              title="다른 부서로 드래그하여 이동할 수 있습니다."
                            >
                              <GripVertical className="w-3 h-3 text-slate-400" />
                              <span>{m.Name}</span>
                              <span className="text-[10px] text-slate-500">
                                ({m.Position || m.JobTitle || '사원'})
                              </span>
                            </div>
                          );
                        })}

                        {deptMembers.length === 0 && (
                          <div className="w-full flex items-center justify-center py-3 text-xs text-slate-400 font-medium">
                            소속된 팀원이 없습니다. 다른 부서에서 사원을 드래그해 오세요.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Tab 2: Department Approval Rules Config */}
      {activeTab === 'rules' && (
        <div className="space-y-6">
          {/* Dynamic Rank Approval Rules Manager */}
          <div className="bg-slate-50 p-6 rounded-2xl border border-slate-200 space-y-4">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pb-2 border-b border-slate-200">
              <div>
                <h3 className="text-base font-extrabold text-slate-900 flex items-center gap-2">
                  <span>⚙️</span> 직급별 자동 결재선 진행 프로세스 관리
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  직급 그룹별 결재 단계(1차/2차/3차...) 및 승인자 유형(부서팀장, 지정이사, 대표이사, 특정사원)을 자유롭게 구성할 수 있습니다.
                </p>
              </div>
              <div className="flex items-center gap-2 flex-wrap">
                <button
                  type="button"
                  onClick={() => {
                    if (confirm('모든 직급별 결재선 규칙을 시스템 기본값으로 초기화하시겠습니까?')) {
                      resetRankApprovalRules();
                      alert('직급별 결재선 규칙이 기본값으로 초기화되었습니다.');
                    }
                  }}
                  className="px-3 py-1.5 rounded-xl text-xs font-bold text-slate-600 bg-white border border-slate-300 hover:bg-slate-100 flex items-center gap-1.5 shadow-2xs cursor-pointer"
                >
                  <RotateCcw className="w-3.5 h-3.5" />
                  <span>기본 룰셋 초기화</span>
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setNewRuleForm({
                      name: '',
                      description: '',
                      targetRanksText: '',
                      stages: [
                        { id: `stg-${Date.now()}-1`, step: 1, roleName: '1차 결재자 (소속팀장)', approverType: 'DEPT_LEAD', specificUserId: null },
                        { id: `stg-${Date.now()}-2`, step: 2, roleName: '최종 결재자 (대표이사)', approverType: 'CEO', specificUserId: null }
                      ]
                    });
                    setIsAddRuleModalOpen(true);
                  }}
                  className="px-3.5 py-1.5 rounded-xl text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 flex items-center gap-1.5 shadow-xs cursor-pointer"
                >
                  <Plus className="w-3.5 h-3.5" />
                  <span>새 직급 그룹 추가</span>
                </button>
              </div>
            </div>

            {/* Rule Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {(rankApprovalRules || []).map((rule, rIdx) => (
                <div
                  key={rule.id || rIdx}
                  className="bg-white p-5 rounded-2xl border border-slate-200/90 shadow-2xs hover:shadow-md transition-all space-y-3.5 flex flex-col justify-between"
                >
                  <div className="space-y-2">
                    {/* Header */}
                    <div className="flex justify-between items-start gap-2">
                      <div className="min-w-0">
                        <span className="font-extrabold text-indigo-900 text-sm block truncate">
                          {rule.name}
                        </span>
                        <p className="text-xs text-slate-500 mt-0.5 leading-snug">
                          {rule.description}
                        </p>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => {
                            setEditingRule(JSON.parse(JSON.stringify(rule)));
                            setIsEditRuleModalOpen(true);
                          }}
                          className="px-2.5 py-1 text-xs font-bold text-indigo-600 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 rounded-lg flex items-center gap-1 cursor-pointer"
                        >
                          <Edit3 className="w-3 h-3" />
                          <span>수정</span>
                        </button>
                        {!rule.isSystem && (
                          <button
                            type="button"
                            onClick={() => {
                              if (confirm(`'${rule.name}' 직급 그룹 결재선 규칙을 삭제하시겠습니까?`)) {
                                const filtered = (rankApprovalRules || []).filter(r => r.id !== rule.id);
                                updateRankApprovalRules(filtered);
                                alert('직급 그룹이 삭제되었습니다.');
                              }
                            }}
                            className="p-1 text-slate-400 hover:text-rose-600 rounded-lg cursor-pointer"
                            title="그룹 삭제"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Target Ranks Tags */}
                    <div className="space-y-1">
                      <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider block">
                        적용 대상 직급 ({rule.targetRanks?.length || 0}개):
                      </span>
                      <div className="flex flex-wrap gap-1">
                        {(rule.targetRanks || []).map((rank, tIdx) => (
                          <span
                            key={tIdx}
                            className="px-2 py-0.5 bg-slate-100 text-slate-700 font-semibold text-[11px] rounded-md border border-slate-200/80"
                          >
                            {rank}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Flow Steps Pipeline */}
                  <div className="pt-3 border-t border-slate-100 space-y-2">
                    <div className="flex justify-between items-center text-[10px] font-bold text-slate-500 uppercase tracking-wider">
                      <span>자동 결재선 진행 단계</span>
                      <span className="text-indigo-600 font-extrabold">총 {rule.stages?.length || 0}단계</span>
                    </div>

                    <div className="flex items-center gap-1.5 flex-wrap">
                      {(rule.stages || []).map((stg, sIdx) => {
                        const isLast = sIdx === (rule.stages?.length || 0) - 1;
                        let approverLabel = '';
                        let badgeColor = 'bg-slate-100 text-slate-700 border-slate-200';

                        if (stg.approverType === 'DEPT_LEAD') {
                          approverLabel = '소속 부서 팀장';
                          badgeColor = 'bg-sky-50 text-sky-800 border-sky-200';
                        } else if (stg.approverType === 'SECONDARY_APPROVER') {
                          approverLabel = '지정 이사 / 임원';
                          badgeColor = 'bg-purple-50 text-purple-800 border-purple-200';
                        } else if (stg.approverType === 'CEO') {
                          approverLabel = '대표이사';
                          badgeColor = 'bg-emerald-50 text-emerald-800 border-emerald-200';
                        } else if (stg.approverType === 'SPECIFIC_USER') {
                          const targetU = users.find(u => u.UserID === stg.specificUserId);
                          approverLabel = targetU ? `${targetU.Name} (${targetU.Position || targetU.JobTitle})` : '지정 사원';
                          badgeColor = 'bg-amber-50 text-amber-800 border-amber-200';
                        }

                        return (
                          <React.Fragment key={stg.id || sIdx}>
                            <div className={`px-2.5 py-1 rounded-lg border text-xs font-bold flex items-center gap-1 shadow-2xs ${badgeColor}`}>
                              <span className="text-[10px] opacity-70 font-extrabold">{sIdx + 1}차:</span>
                              <span>{approverLabel}</span>
                            </div>
                            {!isLast && <ArrowRight className="w-3.5 h-3.5 text-slate-300 flex-shrink-0" />}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Department Lead Approver Selection Form */}
          <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-4">
            <h3 className="text-base font-bold text-slate-900">
              부서별 1차 전담 결재권자(팀장) 지정
            </h3>
            <p className="text-xs text-slate-500">
              소속 부서원이 휴가/경비 결재를 상신했을 때 1차 결재를 수행할 팀장을 부서별로 지정합니다.
            </p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {deptList.map(dept => {
                const deptMembers = users.filter(u => u.Department === dept && u.EmploymentStatus !== '퇴사' && u.Status !== 'Retired');
                const activeLead = deptMembers.find(u => u.UserID === deptLeads[dept]) || deptMembers.find(u => (u.JobTitle || '').includes('팀장') || (u.Position || '').includes('이사') || (u.Position || '').includes('팀장')) || deptMembers[0];
                const currentLeadId = activeLead?.UserID || deptMembers[0]?.UserID || 'emp-01';

                return (
                  <div key={dept} className="p-4 bg-slate-50 border border-slate-200 rounded-xl space-y-2">
                    <label className="text-xs font-bold text-slate-700 uppercase flex items-center justify-between">
                      <span>{dept} 1차 결재자</span>
                      <span className="text-indigo-600 font-semibold">{dept}</span>
                    </label>
                    <select
                      value={currentLeadId}
                      onChange={e => {
                        updateDeptLead(dept, e.target.value);
                        alert(`[${dept}] 부서의 1차 결재자가 변경되었습니다.`);
                      }}
                      className="w-full px-3.5 py-2.5 bg-white border border-slate-300 rounded-xl text-sm font-bold text-slate-900 focus:outline-none focus:border-indigo-600"
                    >
                      {deptMembers.map(m => (
                        <option key={m.UserID} value={m.UserID}>
                          {m.Name} ({m.Position || m.JobTitle}) - {m.Account}
                        </option>
                      ))}
                    </select>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* Tab 3: Custom Preset Templates */}
      {activeTab === 'presets' && (
        <div className="space-y-6">
          {/* Preset list */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {approvalPresets.map(preset => (
              <div
                key={preset.id}
                className="bg-white border border-slate-200 rounded-2xl p-5 shadow-xs space-y-3 relative"
              >
                <div className="flex justify-between items-start">
                  <div>
                    <h4 className="text-base font-bold text-slate-900">{preset.name}</h4>
                    <p className="text-xs text-slate-500 mt-0.5">{preset.description}</p>
                  </div>
                  {preset.isDefault ? (
                    <span className="px-2.5 py-1 rounded-full text-xs font-bold bg-indigo-50 text-indigo-700 border border-indigo-200">
                      시스템 기본
                    </span>
                  ) : (
                    <button
                      onClick={() => handleDeletePreset(preset.id)}
                      className="text-slate-400 hover:text-rose-600 p-1"
                      title="템플릿 삭제"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>

                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100 text-xs font-semibold text-slate-700 flex items-center gap-2">
                  <Layers className="w-4 h-4 text-indigo-600 flex-shrink-0" />
                  <span>결재 상신 시 직원이 선택하거나 관리자가 지정할 수 있는 프리셋입니다.</span>
                </div>
              </div>
            ))}
          </div>

          {/* Add custom template form */}
          <form onSubmit={handleAddPreset} className="bg-white border border-slate-200 rounded-2xl p-6 shadow-xs space-y-4">
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Plus className="w-4 h-4 text-indigo-600" />
              <span>새 결재선 프리셋 등록</span>
            </h3>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 uppercase">프리셋 명칭 *</label>
                <input
                  type="text"
                  placeholder="예: 긴급 프로젝트 전결 결재선"
                  value={newPresetName}
                  onChange={e => setNewPresetName(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 text-slate-900"
                  required
                />
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-bold text-slate-700 uppercase">설명 / 적용 조건</label>
                <input
                  type="text"
                  placeholder="예: 100만원 이상 장비 구매 시 적용"
                  value={newPresetDesc}
                  onChange={e => setNewPresetDesc(e.target.value)}
                  className="w-full px-4 py-2.5 bg-white border border-slate-300 rounded-xl text-sm focus:outline-none focus:border-indigo-600 text-slate-900"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <button
                type="submit"
                className="px-5 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-sm rounded-xl shadow-sm cursor-pointer transition-all"
              >
                결재선 프리셋 추가
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Modal: Add Department */}
      {isAddDeptModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-sm w-full shadow-2xl space-y-4 text-left">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <Building2 className="w-5 h-5 text-indigo-600" />
              <span>새 부서 추가</span>
            </h3>
            <p className="text-xs text-slate-500">
              새로운 사내 조직 부서를 생성합니다.
            </p>

            <form onSubmit={handleAddDepartment} className="space-y-3">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">부서명 *</label>
                <input
                  type="text"
                  placeholder="예: 플랫폼엔지니어링, 보안운영팀"
                  value={newDeptNameInput}
                  onChange={e => setNewDeptNameInput(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs md:text-sm font-medium focus:bg-white focus:outline-none focus:border-indigo-600"
                  required
                  autoFocus
                />
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setIsAddDeptModalOpen(false)}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 cursor-pointer"
                >
                  취소
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow-md cursor-pointer"
                >
                  부서 생성
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Add 2nd-Stage Approver (Requirement 4) */}
      {isAddSecondaryModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-md w-full shadow-2xl space-y-4 text-left animate-in fade-in zoom-in-95 duration-150">
            <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
              <ShieldCheck className="w-5 h-5 text-indigo-600" />
              <span>2차 결재권자(중간 승인자) 추가</span>
            </h3>
            <p className="text-xs text-slate-500">
              사내 결재 시 2단계 검토 및 승인을 담당할 이사/임원/관리자를 추가합니다.
            </p>

            <div className="space-y-3 pt-1">
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">임직원 선택 *</label>
                <select
                  value={selectedSecondaryCandidate}
                  onChange={(e) => setSelectedSecondaryCandidate(e.target.value)}
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs md:text-sm font-semibold focus:bg-white focus:outline-none focus:border-indigo-600"
                >
                  <option value="">-- 결재권자로 지정할 임직원 선택 --</option>
                  {users
                    .filter(u => !secondaryApproverIds.includes(u.UserID) && u.UserID !== 'emp-10')
                    .map(u => (
                      <option key={u.UserID} value={u.UserID}>
                        {u.Name} {u.JobTitle || u.Position || '사원'} ({u.Department} / {u.Email})
                      </option>
                    ))}
                </select>
              </div>

              <div className="p-3 bg-indigo-50/60 border border-indigo-100 rounded-xl text-[11px] text-indigo-900 leading-relaxed">
                💡 2차 승인자로 등록된 인원은 결재선에 포함되어 팀장 승인 후 이어서 검토를 진행하게 됩니다.
              </div>

              <div className="flex items-center justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setSelectedSecondaryCandidate('');
                    setIsAddSecondaryModalOpen(false);
                  }}
                  className="px-4 py-2 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 cursor-pointer"
                >
                  취소
                </button>
                <button
                  type="button"
                  disabled={!selectedSecondaryCandidate}
                  onClick={() => {
                    if (selectedSecondaryCandidate) {
                      addSecondaryApprover(selectedSecondaryCandidate);
                      setSelectedSecondaryCandidate('');
                      setIsAddSecondaryModalOpen(false);
                    }
                  }}
                  className="px-5 py-2 rounded-xl text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-md cursor-pointer"
                >
                  2차 승인자 등록
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Edit Rank Approval Rule */}
      {isEditRuleModalOpen && editingRule && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-2xl w-full shadow-2xl space-y-5 text-left my-8 animate-in fade-in zoom-in-95 duration-150 max-h-[90vh] flex flex-col justify-between">
            {/* Modal Header */}
            <div className="flex justify-between items-start pb-3 border-b border-slate-100 flex-shrink-0">
              <div>
                <h3 className="text-lg font-extrabold text-slate-900 flex items-center gap-2">
                  <span>⚙️</span> 직급별 결재선 프로세스 편집
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  직급 그룹 명칭, 적용 대상 직급, 결재 단계와 승인자 유형을 수정합니다.
                </p>
              </div>
              <button
                type="button"
                onClick={() => {
                  setEditingRule(null);
                  setIsEditRuleModalOpen(false);
                }}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-xl hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="space-y-4 overflow-y-auto pr-1 flex-1">
              {/* Group Name */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">그룹 명칭 *</label>
                <input
                  type="text"
                  value={editingRule.name}
                  onChange={(e) => setEditingRule({ ...editingRule, name: e.target.value })}
                  placeholder="예: 1. 사원 / 대리 / 과장 / 차장 / 책임 (실무진)"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-600"
                />
              </div>

              {/* Description */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">설명</label>
                <input
                  type="text"
                  value={editingRule.description}
                  onChange={(e) => setEditingRule({ ...editingRule, description: e.target.value })}
                  placeholder="결재선에 대한 상세 설명을 입력하세요."
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:bg-white focus:outline-none focus:border-indigo-600"
                />
              </div>

              {/* Target Ranks Tags */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 flex justify-between items-center">
                  <span>적용 대상 직급 (쉼표로 구분) *</span>
                  <span className="text-[10px] text-indigo-600 font-semibold">사원, 대리, 과장 등</span>
                </label>
                <input
                  type="text"
                  value={Array.isArray(editingRule.targetRanks) ? editingRule.targetRanks.join(', ') : (editingRule.targetRanks || '')}
                  onChange={(e) => {
                    const ranks = e.target.value.split(',').map(s => s.trim());
                    setEditingRule({ ...editingRule, targetRanks: ranks });
                  }}
                  placeholder="사원, 주임, 대리, 과장, 차장, 책임, 연구원, 전임, 선임"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none focus:border-indigo-600"
                />
              </div>

              {/* Step-by-Step Approval Stages Builder */}
              <div className="space-y-3 pt-2">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-extrabold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                    <span>📋</span> 결재 단계 설정 ({editingRule.stages?.length || 0}단계)
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const currentStages = editingRule.stages || [];
                      const newStage = {
                        id: `stg-${Date.now()}`,
                        step: currentStages.length + 1,
                        roleName: `${currentStages.length + 1}차 결재자`,
                        approverType: 'DEPT_LEAD',
                        specificUserId: null
                      };
                      setEditingRule({
                        ...editingRule,
                        stages: [...currentStages, newStage]
                      });
                    }}
                    className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-bold rounded-lg border border-indigo-200 flex items-center gap-1 cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>단계 추가</span>
                  </button>
                </div>

                {/* Stages List */}
                <div className="space-y-2.5">
                  {(editingRule.stages || []).map((stage, sIdx) => (
                    <div
                      key={stage.id || sIdx}
                      className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl space-y-2.5 shadow-2xs"
                    >
                      <div className="flex justify-between items-center">
                        <span className="px-2 py-0.5 bg-indigo-600 text-white font-extrabold text-xs rounded-md shadow-2xs">
                          {sIdx + 1}단계 ({sIdx + 1}차 결재)
                        </span>

                        <div className="flex items-center gap-1">
                          {/* Move Up */}
                          <button
                            type="button"
                            disabled={sIdx === 0}
                            onClick={() => {
                              const newStages = [...editingRule.stages];
                              const temp = newStages[sIdx - 1];
                              newStages[sIdx - 1] = newStages[sIdx];
                              newStages[sIdx] = temp;
                              setEditingRule({
                                ...editingRule,
                                stages: newStages.map((st, i) => ({ ...st, step: i + 1 }))
                              });
                            }}
                            className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30 rounded-md hover:bg-slate-200 cursor-pointer"
                            title="위로 이동"
                          >
                            <ArrowUp className="w-3.5 h-3.5" />
                          </button>

                          {/* Move Down */}
                          <button
                            type="button"
                            disabled={sIdx === (editingRule.stages.length - 1)}
                            onClick={() => {
                              const newStages = [...editingRule.stages];
                              const temp = newStages[sIdx + 1];
                              newStages[sIdx + 1] = newStages[sIdx];
                              newStages[sIdx] = temp;
                              setEditingRule({
                                ...editingRule,
                                stages: newStages.map((st, i) => ({ ...st, step: i + 1 }))
                              });
                            }}
                            className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30 rounded-md hover:bg-slate-200 cursor-pointer"
                            title="아래로 이동"
                          >
                            <ArrowDown className="w-3.5 h-3.5" />
                          </button>

                          {/* Delete Stage */}
                          <button
                            type="button"
                            disabled={editingRule.stages.length <= 1}
                            onClick={() => {
                              if (editingRule.stages.length <= 1) {
                                alert('최소 1개의 결재 단계는 유지되어야 합니다.');
                                return;
                              }
                              const filtered = editingRule.stages.filter((_, i) => i !== sIdx);
                              setEditingRule({
                                ...editingRule,
                                stages: filtered.map((st, i) => ({ ...st, step: i + 1 }))
                              });
                            }}
                            className="p-1 text-slate-400 hover:text-rose-600 disabled:opacity-30 rounded-md hover:bg-rose-50 cursor-pointer"
                            title="단계 삭제"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      {/* Stage Fields */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        <div className="space-y-1">
                          <label className="text-[11px] font-bold text-slate-600">역할 명칭</label>
                          <input
                            type="text"
                            value={stage.roleName || ''}
                            onChange={(e) => {
                              const updated = [...editingRule.stages];
                              updated[sIdx].roleName = e.target.value;
                              setEditingRule({ ...editingRule, stages: updated });
                            }}
                            placeholder="예: 1차 결재자 (소속팀장)"
                            className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:border-indigo-600"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="text-[11px] font-bold text-slate-600">승인자 유형</label>
                          <select
                            value={stage.approverType || 'DEPT_LEAD'}
                            onChange={(e) => {
                              const updated = [...editingRule.stages];
                              updated[sIdx].approverType = e.target.value;
                              if (e.target.value !== 'SPECIFIC_USER') {
                                updated[sIdx].specificUserId = null;
                              }
                              setEditingRule({ ...editingRule, stages: updated });
                            }}
                            className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-indigo-600"
                          >
                            <option value="DEPT_LEAD">소속 부서 전담 팀장 (자동 매핑)</option>
                            <option value="SECONDARY_APPROVER">지정 2차 결재자 / 총괄 이사</option>
                            <option value="CEO">대표이사 (최종 승인)</option>
                            <option value="SPECIFIC_USER">특정 사원 직접 지정</option>
                          </select>
                        </div>
                      </div>

                      {/* Specific User Selector if SPECIFIC_USER */}
                      {stage.approverType === 'SPECIFIC_USER' && (
                        <div className="space-y-1 pt-1 border-t border-slate-200">
                          <label className="text-[11px] font-bold text-indigo-700">지정할 결재권자 사원 선택 *</label>
                          <select
                            value={stage.specificUserId || ''}
                            onChange={(e) => {
                              const updated = [...editingRule.stages];
                              updated[sIdx].specificUserId = e.target.value;
                              setEditingRule({ ...editingRule, stages: updated });
                            }}
                            className="w-full px-3 py-2 bg-white border border-indigo-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="">-- 사원 선택 --</option>
                            {users.map(u => (
                              <option key={u.UserID} value={u.UserID}>
                                {u.Name} ({u.Position || u.JobTitle || '사원'}) - {u.Department} [{u.Email}]
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 flex-shrink-0">
              <button
                type="button"
                onClick={() => {
                  setEditingRule(null);
                  setIsEditRuleModalOpen(false);
                }}
                className="px-4 py-2.5 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 cursor-pointer"
              >
                취소
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!editingRule.name) {
                    alert('직급 그룹 명칭을 입력해 주세요.');
                    return;
                  }
                  if (!editingRule.stages || editingRule.stages.length === 0) {
                    alert('최소 1개 이상의 결재 단계가 필요합니다.');
                    return;
                  }

                  const cleanedTargetRanks = (Array.isArray(editingRule.targetRanks) ? editingRule.targetRanks : String(editingRule.targetRanks).split(','))
                    .map(s => s.trim())
                    .filter(Boolean);

                  const updatedRule = {
                    ...editingRule,
                    targetRanks: cleanedTargetRanks,
                    stages: editingRule.stages.map((st, i) => ({ ...st, step: i + 1 }))
                  };

                  const updatedRules = (rankApprovalRules || []).map(r => r.id === updatedRule.id ? updatedRule : r);
                  updateRankApprovalRules(updatedRules);
                  setEditingRule(null);
                  setIsEditRuleModalOpen(false);
                  alert(`'${updatedRule.name}' 결재선 규칙이 저장되었습니다.`);
                }}
                className="px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow-md cursor-pointer flex items-center gap-1.5"
              >
                <Save className="w-3.5 h-3.5" />
                <span>변경사항 저장</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Add New Rank Approval Rule Group */}
      {isAddRuleModalOpen && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-xs flex items-center justify-center p-4 overflow-y-auto">
          <div className="bg-white border border-slate-200 rounded-3xl p-6 max-w-2xl w-full shadow-2xl space-y-5 text-left my-8 animate-in fade-in zoom-in-95 duration-150 max-h-[90vh] flex flex-col justify-between">
            {/* Modal Header */}
            <div className="flex justify-between items-start pb-3 border-b border-slate-100 flex-shrink-0">
              <div>
                <h3 className="text-lg font-extrabold text-slate-900 flex items-center gap-2">
                  <Plus className="w-5 h-5 text-indigo-600" />
                  <span>새 직급 그룹 결재선 등록</span>
                </h3>
                <p className="text-xs text-slate-500 mt-0.5">
                  새로운 직급 그룹을 정의하고 맞춤형 결재 단계 및 승인자를 구성합니다.
                </p>
              </div>
              <button
                type="button"
                onClick={() => setIsAddRuleModalOpen(false)}
                className="text-slate-400 hover:text-slate-600 p-1.5 rounded-xl hover:bg-slate-100 cursor-pointer"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="space-y-4 overflow-y-auto pr-1 flex-1">
              {/* Group Name */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">그룹 명칭 *</label>
                <input
                  type="text"
                  value={newRuleForm.name}
                  onChange={(e) => setNewRuleForm({ ...newRuleForm, name: e.target.value })}
                  placeholder="예: 파트장 / 프로젝트 리더 그룹"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-sm font-bold text-slate-900 focus:bg-white focus:outline-none focus:border-indigo-600"
                />
              </div>

              {/* Description */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700">설명</label>
                <input
                  type="text"
                  value={newRuleForm.description}
                  onChange={(e) => setNewRuleForm({ ...newRuleForm, description: e.target.value })}
                  placeholder="예: 프로젝트 리더 및 파트장 전용 2단계 결재선"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs text-slate-800 focus:bg-white focus:outline-none focus:border-indigo-600"
                />
              </div>

              {/* Target Ranks Tags */}
              <div className="space-y-1">
                <label className="text-xs font-bold text-slate-700 flex justify-between items-center">
                  <span>적용 대상 직급 (쉼표로 구분) *</span>
                  <span className="text-[10px] text-indigo-600 font-semibold">파트장, 리더, PL 등</span>
                </label>
                <input
                  type="text"
                  value={newRuleForm.targetRanksText}
                  onChange={(e) => setNewRuleForm({ ...newRuleForm, targetRanksText: e.target.value })}
                  placeholder="예: 파트장, 리더, PL, PM"
                  className="w-full px-3.5 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-800 focus:bg-white focus:outline-none focus:border-indigo-600"
                />
              </div>

              {/* Step-by-Step Approval Stages Builder */}
              <div className="space-y-3 pt-2">
                <div className="flex justify-between items-center">
                  <label className="text-xs font-extrabold text-slate-800 uppercase tracking-wider flex items-center gap-1.5">
                    <span>📋</span> 결재 단계 설정 ({newRuleForm.stages?.length || 0}단계)
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      const currentStages = newRuleForm.stages || [];
                      const newStage = {
                        id: `stg-${Date.now()}`,
                        step: currentStages.length + 1,
                        roleName: `${currentStages.length + 1}차 결재자`,
                        approverType: 'CEO',
                        specificUserId: null
                      };
                      setNewRuleForm({
                        ...newRuleForm,
                        stages: [...currentStages, newStage]
                      });
                    }}
                    className="px-2.5 py-1 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 text-xs font-bold rounded-lg border border-indigo-200 flex items-center gap-1 cursor-pointer"
                  >
                    <Plus className="w-3.5 h-3.5" />
                    <span>단계 추가</span>
                  </button>
                </div>

                {/* Stages List */}
                <div className="space-y-2.5">
                  {(newRuleForm.stages || []).map((stage, sIdx) => (
                    <div
                      key={stage.id || sIdx}
                      className="p-3.5 bg-slate-50 border border-slate-200 rounded-2xl space-y-2.5 shadow-2xs"
                    >
                      <div className="flex justify-between items-center">
                        <span className="px-2 py-0.5 bg-indigo-600 text-white font-extrabold text-xs rounded-md shadow-2xs">
                          {sIdx + 1}단계 ({sIdx + 1}차 결재)
                        </span>

                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            disabled={sIdx === 0}
                            onClick={() => {
                              const newStages = [...newRuleForm.stages];
                              const temp = newStages[sIdx - 1];
                              newStages[sIdx - 1] = newStages[sIdx];
                              newStages[sIdx] = temp;
                              setNewRuleForm({
                                ...newRuleForm,
                                stages: newStages.map((st, i) => ({ ...st, step: i + 1 }))
                              });
                            }}
                            className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30 rounded-md hover:bg-slate-200 cursor-pointer"
                            title="위로 이동"
                          >
                            <ArrowUp className="w-3.5 h-3.5" />
                          </button>

                          <button
                            type="button"
                            disabled={sIdx === (newRuleForm.stages.length - 1)}
                            onClick={() => {
                              const newStages = [...newRuleForm.stages];
                              const temp = newStages[sIdx + 1];
                              newStages[sIdx + 1] = newStages[sIdx];
                              newStages[sIdx] = temp;
                              setNewRuleForm({
                                ...newRuleForm,
                                stages: newStages.map((st, i) => ({ ...st, step: i + 1 }))
                              });
                            }}
                            className="p-1 text-slate-400 hover:text-slate-700 disabled:opacity-30 rounded-md hover:bg-slate-200 cursor-pointer"
                            title="아래로 이동"
                          >
                            <ArrowDown className="w-3.5 h-3.5" />
                          </button>

                          <button
                            type="button"
                            disabled={newRuleForm.stages.length <= 1}
                            onClick={() => {
                              if (newRuleForm.stages.length <= 1) {
                                alert('최소 1개의 결재 단계는 유지되어야 합니다.');
                                return;
                              }
                              const filtered = newRuleForm.stages.filter((_, i) => i !== sIdx);
                              setNewRuleForm({
                                ...newRuleForm,
                                stages: filtered.map((st, i) => ({ ...st, step: i + 1 }))
                              });
                            }}
                            className="p-1 text-slate-400 hover:text-rose-600 disabled:opacity-30 rounded-md hover:bg-rose-50 cursor-pointer"
                            title="단계 삭제"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      </div>

                      {/* Stage Fields */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
                        <div className="space-y-1">
                          <label className="text-[11px] font-bold text-slate-600">역할 명칭</label>
                          <input
                            type="text"
                            value={stage.roleName || ''}
                            onChange={(e) => {
                              const updated = [...newRuleForm.stages];
                              updated[sIdx].roleName = e.target.value;
                              setNewRuleForm({ ...newRuleForm, stages: updated });
                            }}
                            placeholder="예: 1차 결재자 (소속팀장)"
                            className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-semibold text-slate-800 focus:outline-none focus:border-indigo-600"
                          />
                        </div>

                        <div className="space-y-1">
                          <label className="text-[11px] font-bold text-slate-600">승인자 유형</label>
                          <select
                            value={stage.approverType || 'DEPT_LEAD'}
                            onChange={(e) => {
                              const updated = [...newRuleForm.stages];
                              updated[sIdx].approverType = e.target.value;
                              if (e.target.value !== 'SPECIFIC_USER') {
                                updated[sIdx].specificUserId = null;
                              }
                              setNewRuleForm({ ...newRuleForm, stages: updated });
                            }}
                            className="w-full px-3 py-2 bg-white border border-slate-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:border-indigo-600"
                          >
                            <option value="DEPT_LEAD">소속 부서 전담 팀장 (자동 매핑)</option>
                            <option value="SECONDARY_APPROVER">지정 2차 결재자 / 총괄 이사</option>
                            <option value="CEO">대표이사 (최종 승인)</option>
                            <option value="SPECIFIC_USER">특정 사원 직접 지정</option>
                          </select>
                        </div>
                      </div>

                      {/* Specific User Selector if SPECIFIC_USER */}
                      {stage.approverType === 'SPECIFIC_USER' && (
                        <div className="space-y-1 pt-1 border-t border-slate-200">
                          <label className="text-[11px] font-bold text-indigo-700">지정할 결재권자 사원 선택 *</label>
                          <select
                            value={stage.specificUserId || ''}
                            onChange={(e) => {
                              const updated = [...newRuleForm.stages];
                              updated[sIdx].specificUserId = e.target.value;
                              setNewRuleForm({ ...newRuleForm, stages: updated });
                            }}
                            className="w-full px-3 py-2 bg-white border border-indigo-300 rounded-xl text-xs font-bold text-slate-900 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                          >
                            <option value="">-- 사원 선택 --</option>
                            {users.map(u => (
                              <option key={u.UserID} value={u.UserID}>
                                {u.Name} ({u.Position || u.JobTitle || '사원'}) - {u.Department} [{u.Email}]
                              </option>
                            ))}
                          </select>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="flex items-center justify-end gap-2 pt-3 border-t border-slate-100 flex-shrink-0">
              <button
                type="button"
                onClick={() => setIsAddRuleModalOpen(false)}
                className="px-4 py-2.5 rounded-xl text-xs font-bold text-slate-600 hover:bg-slate-100 cursor-pointer"
              >
                취소
              </button>
              <button
                type="button"
                onClick={() => {
                  if (!newRuleForm.name) {
                    alert('직급 그룹 명칭을 입력해 주세요.');
                    return;
                  }
                  if (!newRuleForm.targetRanksText) {
                    alert('적용 대상 직급을 최소 1개 이상 입력해 주세요.');
                    return;
                  }
                  if (!newRuleForm.stages || newRuleForm.stages.length === 0) {
                    alert('최소 1개 이상의 결재 단계가 필요합니다.');
                    return;
                  }

                  const ranks = newRuleForm.targetRanksText
                    .split(',')
                    .map(s => s.trim())
                    .filter(Boolean);

                  const newRule = {
                    id: `rule-custom-${Date.now()}`,
                    name: newRuleForm.name,
                    description: newRuleForm.description || '커스텀 직급 결재선 규칙',
                    targetRanks: ranks,
                    isSystem: false,
                    stages: newRuleForm.stages.map((st, i) => ({ ...st, step: i + 1 }))
                  };

                  const updatedRules = [...(rankApprovalRules || []), newRule];
                  updateRankApprovalRules(updatedRules);
                  setIsAddRuleModalOpen(false);
                  alert(`'${newRule.name}' 새 결재선 그룹이 등록되었습니다.`);
                }}
                className="px-5 py-2.5 rounded-xl text-xs font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow-md cursor-pointer flex items-center gap-1.5"
              >
                <Check className="w-3.5 h-3.5" />
                <span>직급 그룹 등록</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default OrganizationManagement;
